// Define the API base URL based on environment
export const API_BASE_URL = process.env.NODE_ENV === 'production' 
  ? '' // Use relative URLs in production for path-based routing
  : 'http://localhost:5001';

// Get the access token from localStorage
export const getAccessToken = () => {
  return localStorage.getItem('accessToken');
};

// Common subdomains that are not school identifiers
// Updated for better production subdomain handling
const COMMON_SUBDOMAINS = ['www', 'api', 'mail', 'smtp', 'admin'];

// Helper to extract subdomain from current URL
export const extractSubdomain = () => {
  try {
    // Original production subdomain extraction logic - commented out but preserved for future use
    /*
    if (process.env.NODE_ENV === 'production') {
      // In production, extract from hostname
      const hostname = window.location.hostname;
      console.log('[extractSubdomain] Current hostname:', hostname);
      
      const parts = hostname.split('.');
      console.log('[extractSubdomain] Hostname parts:', parts);
      
      // Check if we have a subdomain (domain.com vs subdomain.domain.com)
      if (parts.length > 2) {
        // Verify it's not literally "undefined" or a common subdomain
        const potentialSubdomain = parts[0];
        
        // Check if this is www or another common subdomain
        if (COMMON_SUBDOMAINS.includes(potentialSubdomain)) {
          console.log('[extractSubdomain] Common subdomain detected:', potentialSubdomain);
          return null;
        }
        
        if (potentialSubdomain && potentialSubdomain !== 'undefined') {
          console.log('[extractSubdomain] Found subdomain from hostname:', potentialSubdomain);
          return potentialSubdomain;
        }
      }
      
      return null;
    }
    */
      
    // Use path-based routing for both development and production
    const path = window.location.pathname;
    const pathParts = path.split('/');
    
    console.log('[extractSubdomain] Checking pathname:', path);
    
    // Check for root path - should not be considered a subdomain route
    if (path === '/' || path === '') {
      console.log('[extractSubdomain] Root path detected, not a subdomain route');
      return null;
    }
    
    // If the first path segment is not a reserved word, it's our "subdomain"
    if (pathParts.length > 1 && pathParts[1]) {
      const potentialSubdomain = pathParts[1];
      
      // Check if the potential subdomain is not a reserved path and not literally "undefined"
      const reservedPaths = [
        // Authentication routes
        'login', 'signup', 'dashboard', 'admin',
        // Password management routes
        'forgot-password', 'reset-password', 'verify-email',
        // System routes
        'api', 'undefined', 'static', 'favicon.ico',
        // Asset routes
        'assets', 'css', 'js', 'images',
        // Common web files
        'robots.txt', 'sitemap.xml', 'manifest.json',
        // Reserved words for future use
        'app', 'help', 'support', 'account', 'settings', 'profile',
        'about', 'contact', 'privacy', 'terms', 'faq', 'pricing'
      ];
      if (!reservedPaths.includes(potentialSubdomain)) {
        // Store in sessionStorage to ensure consistency across page refreshes
        sessionStorage.setItem('lastDetectedSubdomain', potentialSubdomain);
        console.log('[extractSubdomain] Found subdomain from path:', potentialSubdomain);
        return potentialSubdomain;
      }
    }
    
    // If not found in path but we have a previously detected subdomain in sessionStorage, use that
    const savedSubdomain = sessionStorage.getItem('lastDetectedSubdomain');
    if (savedSubdomain) {
      // Verify the current path is compatible with a subdomain route
      // If we're on an admin page, it's likely we should be using the subdomain
      if (path.includes('/admin') || (path !== '/' && path.startsWith('/' + savedSubdomain))) {
        console.log('[extractSubdomain] Using saved subdomain from sessionStorage:', savedSubdomain);
        return savedSubdomain;
      }
    }
    
    return null;
  } catch (error) {
    console.error('[extractSubdomain] Error extracting subdomain:', error);
    return null;
  }
};

// Get the login redirect URL based on current host
export const getLoginUrl = () => {
  const subdomain = extractSubdomain();
  
  // If we have a subdomain, redirect to the subdomain-specific login
  if (subdomain) {
    // Always use path-based format regardless of environment
    return `/${subdomain}/login`;
  }
  
  // Otherwise redirect to the main domain login
  return '/login';
};

// Get the admin URL for the current school context
export const getAdminUrl = (subdomain = null) => {
  // Use the provided subdomain, or extract from the current URL
  const currentSubdomain = subdomain || extractSubdomain();
  
  if (currentSubdomain) {
    // Always use path-based format regardless of environment
    return `/${currentSubdomain}/admin`;
  }
  
  // If no subdomain, redirect to the main dashboard
  // This is a safety fallback and shouldn't normally be used
  return '/dashboard';
};

// Generate a URL for a specific school's admin dashboard
export const getSchoolAdminUrl = (subdomain) => {
  if (!subdomain) return '/dashboard';
  
  // Always use path-based format regardless of environment
  return `/${subdomain}/admin`;
};

/**
 * This establishes the user's current school context
 * @param {Object} user - The user object to update
 * @returns {Object} - The updated user object
 */
export const refreshUserSchoolContext = (user) => {
  if (!user) return user;
  
  // Copy the user to avoid modifying the original
  const updatedUser = { ...user };
  
  // If no current_school is set but schools are available, set the first one as current
  if (!updatedUser.current_school && updatedUser.schools && updatedUser.schools.length > 0) {
    updatedUser.current_school = updatedUser.schools[0];
    console.log('Set user.current_school to:', updatedUser.current_school.name, `(ID: ${updatedUser.current_school.id})`);
  }
  
  return updatedUser;
};

export const refreshAccessToken = async () => {
  const refreshToken = localStorage.getItem('refreshToken');
  if (!refreshToken) {
    console.error('No refresh token available in localStorage');
    throw new Error('No refresh token available');
  }
  
  try {
    console.log('Attempting to refresh token with:', refreshToken.substring(0, 10) + '...');
    
    const res = await fetch(`${API_BASE_URL}/api/refresh`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refreshToken }),
    });
    
    if (!res.ok) {
      const errorText = await res.text();
      console.error('Token refresh failed:', errorText);
      // Clear tokens on refresh failure to force re-login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      throw new Error(`Refresh failed: ${errorText}`);
    }
    
    const data = await res.json();
    console.log('Token refresh response:', data);
    
    if (!data.accessToken) {
      console.error('No access token in refresh response');
      throw new Error('Invalid refresh response');
    }
    
    localStorage.setItem('accessToken', data.accessToken);
    console.log('Token refreshed successfully:', data.accessToken.substring(0, 10) + '...');
    
    // Check if a new refresh token was provided and store it
    if (data.refreshToken) {
      localStorage.setItem('refreshToken', data.refreshToken);
      console.log('Stored new rotated refresh token:', data.refreshToken.substring(0, 10) + '...');
    } else {
      // If no new refresh token is received (e.g., due to an error or older backend version), 
      // it's safer to clear the existing one to force re-login.
      console.warn('No new refresh token received from /api/refresh. Clearing existing token.');
      localStorage.removeItem('refreshToken');
    }
    
    // Update stored user data if present in the response
    if (data.user) {
      // Ensure user has school_id property set correctly
      const updatedUser = refreshUserSchoolContext(data.user);
      
      // Update the stored user in localStorage
      localStorage.setItem('user', JSON.stringify(updatedUser));
      console.log('Updated user data in localStorage with correct school_id');
    }
    
    return data.accessToken;
  } catch (error) {
    console.error('Token refresh error:', error);
    // Ensure tokens are cleared on any error
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    throw error;
  }
};

export const fetchWithRefresh = async (url, options = {}) => {
  const accessToken = localStorage.getItem('accessToken');
  console.log(`Sending request to ${url} with token: ${accessToken?.substring(0, 10)}...`);
  
  try {
    // Determine if this is a FormData request (multipart/form-data)
    const isFormData = options.body instanceof FormData;
    
    // Add cache-busting for dynamic endpoints
    let modifiedUrl = url;
    const dynamicEndpoints = [
      '/api/admin/menus',
      '/api/menus',
      '/api/admin/dishes',
      '/api/dishes',
      '/api/menu-type-courses',
      '/api/admin/menu-type-courses',
      '/api/schools', // Add this to ensure schools endpoints get cache busting
      '/api/nutrients', // Add this to ensure nutrients endpoints get cache busting
      '/api/tags'  // Add this to ensure tags endpoints get cache busting
    ];
    
    // Only add cache-busting for GET requests to dynamic endpoints
    if ((!options.method || options.method === 'GET') && 
        dynamicEndpoints.some(endpoint => modifiedUrl.includes(endpoint))) {
      // Add timestamp parameter to bust browser cache
      const separator = modifiedUrl.includes('?') ? '&' : '?';
      modifiedUrl = `${modifiedUrl}${separator}_t=${Date.now()}`;
      console.log(`Added cache-busting to URL: ${modifiedUrl}`);
    }
    
    // Prepare headers - only set Content-Type for non-FormData requests
    const headers = { 
      ...options.headers, 
      Authorization: accessToken ? `Bearer ${accessToken}` : '',
      // Add cache control headers
      'Cache-Control': 'no-cache, no-store, must-revalidate',
      'Pragma': 'no-cache',
      'Expires': '0'
    };
    
    // Only set Content-Type for non-FormData requests
    if (!isFormData && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    
    let response = await fetch(modifiedUrl, {
      ...options,
      headers,
      // Add cache: 'no-store' to ensure no caching
      cache: 'no-store'
    });
    
    console.log(`Response status for ${modifiedUrl}: ${response.status}`);
    
    if (response.status === 401) {
      console.log('Token expired, attempting refresh');
      try {
        const newToken = await refreshAccessToken();
        console.log(`Got new token: ${newToken?.substring(0, 10)}...`);
        
        if (!newToken) {
          console.error('Refresh succeeded but no new token received');
          window.location.href = getLoginUrl();
          throw new Error('No token after refresh');
        }
        
        // Prepare headers again for the retry
        const retryHeaders = { 
          ...options.headers, 
          Authorization: `Bearer ${newToken}`,
        };
        
        // Only set Content-Type for non-FormData requests in the retry
        if (!isFormData && !retryHeaders['Content-Type']) {
          retryHeaders['Content-Type'] = 'application/json';
        }
        
        // Retry the request with new token
        response = await fetch(modifiedUrl, {
          ...options,
          headers: retryHeaders
        });
        console.log(`Retry response status for ${modifiedUrl}: ${response.status}`);
        
        // If we still get 401 after refresh, redirect to login
        if (response.status === 401) {
          console.error('Still unauthorized after token refresh, redirecting to login');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          window.location.href = getLoginUrl();
          throw new Error('Authentication failed after token refresh');
        }
      } catch (refreshError) {
        console.error('Failed to refresh token:', refreshError);
        // If we can't refresh the token, redirect to login
        window.location.href = getLoginUrl();
        throw refreshError;
      }
    }
    
    return response;
  } catch (error) {
    console.error(`Error fetching ${url}:`, error);
    throw error;
  }
}; 